/* eslint-disable */

import {
    consumeAsciiNumber, consumeBlockEnd, consumeBlockStart, consumeCurrentToken,
    consumeExpression, consumeIfSpaceElseIgnore, consumeLabel, consumeList, consumeNumber,
    consumeRegisterOrMemory, consumeSpace, consumeString, isConsumableSymbolOf,
    parseNested, peekForEndPosition, TSyntaxTree, TSyntaxTreeBreak, TSyntaxTreeBuilderContext,
    TSyntaxTreeConstant, TSyntaxTreeContinue, TSyntaxTreeDebugPoint, TSyntaxTreeExit, TSyntaxTreeFunction,
    TSyntaxTreeIf, TSyntaxTreeInstruction, TSyntaxTreeLabel, TSyntaxTreeMemory, TSyntaxTreeNumber,
    TSyntaxTreeProgram, TSyntaxTreeRegister, TSyntaxTreeTerminate, TSyntaxTreeWhile
} from "./ElfAst";

import {
    buildCodeMap, buildEagerInlineWrapper, buildExpression, buildGenericInstructionExpressionArg,
    buildGenericInstructionListArg, buildGenericInstructionStringArg, buildGenericInstructionWrapper,
    buildGenericNumericAccumulatorInstructionWrapper, buildProgram, buildWrapper, compileExe, injectWrapperDebuggerAfter,
    injectWrapperDebuggerBefore, TElfListCycles, TElfMemRegPtrValuesCycles, TElfStringCycles
} from "./ElfRuntime";

import { AstCompileError } from "./ElfCompileErrors";
import { endLineCol, TToken } from "./ElfTokenizer";


const globalInstructionsBatchSize = 5;
const globalStringBatchSize = globalInstructionsBatchSize * 3;
const globalStringCyclesPerBatch = 5;
const globalListBatchSize = globalInstructionsBatchSize;
const globalListCyclesPerBatch = 5;
const globalMemoryReferenceReadCycles = 1;
const globalRegisterReferenceReadCycles = 1;


const globalStringCycles: TElfStringCycles = {
    batchSize: globalStringBatchSize,
    cyclesPerBatch: globalStringCyclesPerBatch
}

const globalMemRegRefValuesCycles: TElfMemRegPtrValuesCycles = {
    refMemory: globalMemoryReferenceReadCycles,
    refRegister: globalRegisterReferenceReadCycles,
}

const globalListMemRegOnlyCycles = {
    batchSize: globalListBatchSize,
    cyclesPerBatch: globalListCyclesPerBatch,
    memRegOnly: globalMemRegRefValuesCycles
} satisfies TElfListCycles

const globalListExprCycles = {
    batchSize: globalListBatchSize,
    cyclesPerBatch: globalListCyclesPerBatch,
    memRegOnly: false
} satisfies TElfListCycles;


const CYCLES = {
    KEYWORDS: {
        IF: 5,
        WHILE: 5,
        CONTINUE: 3,
        BREAK: 3,
        EXIT: 6,
        TERMINATE: 100,
        DEBUGPOINT: 0,
        CONSTANT: 0, // compile-time
        FUNCTION: 0 // compile-time (call has cost!)
    },
    INSTRUCTIONS: {
        ANY_BITWISE_OP: 1,
        STACK_SIZE: 2,
        ADD: 2,
        SUB: 2,
        MUL: 10,
        DIV: 30,
        GT: 2,
        GTE: 2,
        LT: 2,
        LTE: 2,
        EQ: 2,
        NEQ: 2,
        SET: 2,
        TRUNC: 3,
        PUSH: 3,
        PUSH_R: 3,
        POP: 3,
        POP_R: 3,
        PROMPT: 6,
        CALL: 10,
        PRINT_TEXT: 6,
        PRINTLN_TEXT: 6,
        PRINT: 6,
        PRINTLN: 6
    },
    CONSTANT_READ_WRITE: 1,
    REG_READ_WRITE: 2,
    MEMORY_READ_WRITE: 3,
    STRING: globalStringCycles,
    MEM_REG_ONLY: globalMemRegRefValuesCycles,
    LIST_EXPR: globalListExprCycles,
    LIST_MEM_REG_ONLY: globalListMemRegOnlyCycles
} as const




const ARG_TYPES_MAP = {
    'REGISTER_MEMORY': (argName: string, argDescription: string) => ({ type: 'REGISTER_MEMORY', argName, argDescription, argToken: '<ref{register|pomnilnik}>' }),
    'NUMBER': (argName: string, argDescription: string) => ({ type: 'NUMBER', argName, argDescription, argToken: '<število|izraz>' }),
    'LABEL': (argName: string, argDescription: string) => ({ type: 'LABEL', argName, argDescription, argToken: '<oznaka>' }),
    'FUNCTION_LABEL': (argName: string, argDescription: string) => ({ type: 'FUNCTION_LABEL', argName, argDescription, argToken: '<funkcija>' }),
    'CONST_NUMBER': (argName: string, argDescription: string) => ({ type: 'CONST_NUMBER', argName, argDescription, argToken: '<konstanta_(število|oznaka)>' }),
    'STRING': (argName: string, argDescription: string) => ({ type: 'STRING', argName, argDescription, argToken: '<niz>' }),
    'LIST': {
        'NUMBER': (argName: string, argDescription: string) => {
            const child = ARG_TYPES_MAP.NUMBER(argName, argDescription);
            return { ...child, type: 'LIST_' + child.type, argToken: `<list{${child.argToken}}>` }
        },
        'REGISTER_MEMORY': (argName: string, argDescription: string) => {
            const child = ARG_TYPES_MAP.REGISTER_MEMORY(argName, argDescription);
            return { ...child, type: 'LIST_' + child.type, argToken: `<list{${child.argToken}}>` }
        }
    }
}

function batchAsCyclesDescription(batch: { batchSize: number, cyclesPerBatch: number }): string {
    return `MAX(1, CEIL(število_enot / ${batch.batchSize})) * ${batch.cyclesPerBatch}`
}

function listRefEvalArgNameCyclesDescription(argName: string): string {
    return `${CYCLES.REG_READ_WRITE} IF ${argName}:@register OR ${CYCLES.MEMORY_READ_WRITE} IF ${argName}:[pomnilnik]`
}

function listRefEvalAsCyclesDescription(): string {
    return `SUM_ARGS(${CYCLES.REG_READ_WRITE} IF arg:@register OR ${CYCLES.MEMORY_READ_WRITE} IF arg:[pomnilnik])`
}
function listRefAsCyclesDescription(refs: TElfMemRegPtrValuesCycles): string {
    return `SUM_ARGS(${refs.refRegister} IF arg:@register OR ${refs.refMemory} IF arg:[pomnilnik])`
}

const arithmeticArgsDescription = [
    [ARG_TYPES_MAP.NUMBER('leva', 'Leva vrednost izraza')],
    [ARG_TYPES_MAP.NUMBER('desna', 'Desna vrednost izraza')]
];

const arithmeticBinaryArgsDescription = [
    [ARG_TYPES_MAP.NUMBER('leva', 'Leva vrednost izraza')],
    [ARG_TYPES_MAP.NUMBER('desna', 'Desna vrednost izraza')]
];

const arithmeticBinaryOneArgDescription = [
    [ARG_TYPES_MAP.NUMBER('vrednost', 'vrednost izraza')],
];


function arithmeticBinaryDescription(operation: string, explainAdditional: string = "", isSingle: boolean = false): string {
    const explainProcess = '(pri bitnih operacijah se števila obravnavajo kot 32-bitne binarne vrednosti. Upošteva se le spodnjih 32 bitov števila. Negativne vrednosti so predstavljene v obliki dvojiškega komplementa)';
    const describe = isSingle ? `${operation} arg` : `leva ${operation} desna`;
    return `Izvede binarno operacijo "rezultat = ${describe}"${explainAdditional} nad argumentom${isSingle ? '' : 'a'} in rezultat zapiše v akumulator ${explainProcess}`
}

function arithmeticDescription(operation: string): string {
    return `Izvede matematično operacijo "rezultat = leva ${operation} desna" nad argumentoma in rezultat zapiše v akumulator`
}

function condArithmeticDescription(operation: string): string {
    return `Izvede matematično operacijo "rezultat = leva ${operation} desna" nad argumentoma in rezultat (1 če je izraz resničen, sicer 0) zapiše v akumulator`
}



export function build(args: { maxNumberOfRegisters: number }): TSyntaxTreeBuilderContext {
    const astContext: TSyntaxTreeBuilderContext = {
        builderContext: {
            constantCallsMap: {},
            constantsMap: {},
            functionCallsMap: {},
            functionsMap: {},
            maxNumberOfRegisters: args.maxNumberOfRegisters
        },
        languageContext: {
            meta: {
                batches: {
                    list: {
                        name: 'seznam (list)',
                        cyclesDescription: `${batchAsCyclesDescription(CYCLES.LIST_EXPR)} + (pogojno, če inštrukcija jemlje le reference: ${listRefAsCyclesDescription(CYCLES.LIST_MEM_REG_ONLY.memRegOnly)})`,
                        description: 'Seznam (zapišemo ga kot: { element1 element2 element3 ... } ) se uporablja z inštrukcijami, ki ga podpirajo za optimizacijo prenosa večje količine podatkov. Tako lahko seznam zavzema različno dolžino izrazov, ki so med seboj ločeni s presledkom (iz dokumentacije izbrane inštrukcije je razvidno, če podpira seznam).'
                    },
                    string: {
                        name: 'niz',
                        cyclesDescription: `${batchAsCyclesDescription(CYCLES.STRING)}`,
                        description: 'Niz (zapišemo ga kot: `moj poljuben niz`) lahko vsebuje samo ascii znake in se uporablja z inštrukcijami, ki ga podpirajo za optimizacijo prenosa večje količine podatkov. Tako lahko niz zavzema različno dolžino znakov (iz dokumentacije izbrane inštrukcije je razvidno, če podpira niz).'
                    }
                },
                references: {
                    name: 'referenca (ref)',
                    cyclesDescription: `+${globalMemRegRefValuesCycles.refRegister} IF @register OR +${globalMemRegRefValuesCycles.refMemory} IF [pomnilnik]`,
                    description: `Nekatere inštrukcije lahko v seznamih ali argumentih dovolijo reference. Referenca je lahko samo do pomnilnika in registra. Reference niso vidno označene v kodi (so pa razvidne v dokumentaciji inštrukcije pri podanih argumentih), le pri evaluaciji vzame referenca na pomnilnik +${globalMemRegRefValuesCycles.refMemory} cikel, na register pa +${globalMemRegRefValuesCycles.refRegister} cikel (namesto tipične cene branja / pisanja iz pomnilnika ali registra)`
                }
            },
            primitives: {
                'NUMBER': {
                    metadata: {
                        name: 'število',
                        cyclesDescription: `${CYCLES.CONSTANT_READ_WRITE}`,
                        description: 'Osnovni numerični zapis, ki predstavlja števila s plavajočo vejico, vendar lahko z njim zapišemo tudi cela števila ali ascii vrednosti (#\'A\' - predstavlja vrednost 65). Interno se število shranjuje kot 64-bitni binarni format IEEE 754 z dvojno natančnostjo.',
                        example: '31.5'
                    },
                    transform(tree, opts) { return buildLanguagePrimitiveNumberWrapper(tree as TSyntaxTreeNumber, opts); },
                },
                'LABEL': {
                    metadata: {
                        name: 'konstantna oznaka (števila ali funkcije)',
                        cyclesDescription: `${CYCLES.CONSTANT_READ_WRITE}`,
                        description: 'Ob prevajanju se preslika v numerično konstanto, ki ga je definirala. Izjema je le pri klicu funkcije, kjer se bo sklicevala na definirano funkcijo z ujemajočo oznako',
                        example: 'poljubna_konstanta'
                    },
                    transform(tree, opts) { return buildLanguagePrimitiveLabelConstantWrapper(tree as TSyntaxTreeLabel, opts); },
                },
                'REGISTER': {
                    metadata: {
                        name: 'register',
                        cyclesDescription: `${CYCLES.REG_READ_WRITE}`,
                        description: `V programu se lahko uporablja do ${args.maxNumberOfRegisters} (oz. odvisno od nastavitve prevajalnika) registrov (+1 akumulatorski register, do katerega lahko dostopamo tako, da ne določimo indeksa: '@')  (interno se register shranjuje kot 64-bitni binarni format IEEE 754 z dvojno natančnostjo)`,
                        example: '@1'
                    },
                    transform(tree, opts) { return buildLanguagePrimitiveRegisterWrapper(tree as TSyntaxTreeRegister, opts); },
                },
                'MEMORY': {
                    metadata: {
                        name: 'pomnilnik',
                        cyclesDescription: `${CYCLES.MEMORY_READ_WRITE}`,
                        description: `Definiramo ga z oglatimi oklepaji in znotraj njih določimo pozicijo v pomnilniku, ki jo želimo dostopati - pozor cikli se štejejo tudi pri določitvi mesta. Pri dostopu do pomnilnka se bo vedno vzel le cel del števila (interno se pomnilnik shranjuje kot 64-bitni binarni format IEEE 754 z dvojno natančnostjo)`,
                        example: '[42]'
                    },
                    transform(tree, opts) { return buildLanguagePrimitiveMemoryWrapper(tree as TSyntaxTreeMemory, opts); },
                }
            },
            keywords: {
                'CONSTANT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.CONSTANT}`,
                        modifiesAccumulator: false,
                        arguments: [
                            [ARG_TYPES_MAP.LABEL('ime_konstante', 'Definira ime konstante kot oznako, na katero se lahko sklicuje')],
                            [ARG_TYPES_MAP.CONST_NUMBER('vrednost', 'Definira vrednost konstante, ki jo bo vračala oznaka')]
                        ],
                        description: 'Definira konstantno v programu, ter jo asociira z oznako. Ta se nato sklicuje na konstantno število. Konstante so lahko definirane samo v korenu programa',
                        example: 'CONSTANT moja_konstanta 42'
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordConst(ast, ctx, token, tokens);
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordConstWrapper(tree as TSyntaxTreeConstant, opts.debug);
                    },
                },
                'TERMINATE': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.TERMINATE}`,
                        modifiesAccumulator: true,
                        arguments: [[ARG_TYPES_MAP.NUMBER('koda_napake', 'Koda s katero se zaključi program')]],
                        description: 'Ukaz nemudoma terminira ves program, ter vrne kodo napake, ki je podana. Če je koda napake 0, se sklepa da se je program uspešno zaključil - sicer gre za napako ob izvajanju!',
                        example: 'TERMINATE 0'
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordTerminate(ast, ctx, token, tokens)
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordTerminateWrapper(tree as TSyntaxTreeTerminate, opts.astBuildContext, opts.debug)
                    }
                },
                'IF': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.IF}`,
                        modifiesAccumulator: false,
                        arguments: [[ARG_TYPES_MAP.NUMBER('pogoj', 'Izraz ki se bo preveril pred izvajanjem stavka')]],
                        description: 'Definira pogojni stavek v programu, ki se izvaja pod pogojem. Nato v bloku "{" ...koda... "}" slede izvedljiva koda. Stavek se bo izvedel, če bo vrednost podanega argumenta različna od 0. Če je vrednost enaka 0, se lahko (če je definiran) izvede ELSE "{" ...koda... "}" stavek.',
                        example: 'IF 0 { (se ne izvede) } ELSE { (se izvede) }'
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordIf(ast, ctx, token, tokens)
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordIfWrapper(tree as TSyntaxTreeIf, opts.astBuildContext, opts.debug)
                    }
                },
                'WHILE': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.WHILE}`,
                        modifiesAccumulator: false,
                        arguments: [[ARG_TYPES_MAP.NUMBER('pogoj', 'Izraz ki se bo preveril ob vsaki iteraciji zanke')]],
                        description: 'Definira zanko v programu, ki se izvaja pod pogojem. Nato v bloku "{" ...koda... "}" slede izvedljiva koda. Zanka se bo izvajala tako dolgo dokler bo vrednost podanega argumenta različna od 0',
                        example: 'WHILE 1 { (neskončna zanka) }'
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordWhile(ast, ctx, token, tokens)
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordWhileWrapper(tree as TSyntaxTreeWhile, opts.astBuildContext, opts.debug)
                    }
                },
                'CONTINUE': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.CONTINUE}`,
                        modifiesAccumulator: false,
                        arguments: [],
                        description: 'Ukaz gre uporabiti le v WHILE bloku. S tem ukazom program skoči na začetek WHILE stavka.',
                        example: 'CONTINUE'
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordContinue(ast, ctx, token, tokens);
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordContinueWrapper(tree as TSyntaxTreeContinue, opts.debug);
                    },
                },
                'BREAK': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.BREAK}`,
                        modifiesAccumulator: false,
                        arguments: [],
                        description: 'Ukaz gre uporabiti le v WHILE bloku. S tem ukazom program prekine blok WHILE stavka.',
                        example: 'BREAK'
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordBreak(ast, ctx, token, tokens)
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordBreakWrapper(tree as TSyntaxTreeBreak, opts.debug);
                    },
                },
                'FUNCTION': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.FUNCTION}`,
                        modifiesAccumulator: false,
                        arguments: [[ARG_TYPES_MAP.LABEL('ime_funkcije', 'Ime funkcije, ki jo želiš ustvariti')]],
                        description: 'Definira funkcijo v programu, ter jo asociira z oznako. Nato v bloku "{" ...koda... "}" sledi izvedljiva koda. Funkcije so lahko definirane samo v korenu samega programa',
                        example: 'FUNCTION moja_funkcija { EXIT 42 }'
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordFunction(ast, ctx, token, tokens)
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordFunctionWrapper(tree as TSyntaxTreeFunction, opts.astBuildContext, opts.debug)
                    }
                },
                'EXIT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.EXIT}`,
                        modifiesAccumulator: true,
                        arguments: [[ARG_TYPES_MAP.NUMBER('vrednost', 'nastavi vrednost akumulatorskega (glavnega) registra')]],
                        description: 'Ukaz gre uporabiti le v FUNCTION bloku. S tem ukazom program prekine funkcijo ter zapiše rezultat v akumulatorski (glavni) register',
                        example: 'EXIT 42'
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordExit(ast, ctx, token, tokens)
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordExitWrapper(tree as TSyntaxTreeExit, opts.astBuildContext, opts.debug)
                    }
                },
                'DEBUGPOINT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.KEYWORDS.DEBUGPOINT}`,
                        modifiesAccumulator: false,
                        arguments: [],
                        description: 'Koristi le v "debug" (razhroščevalnem) načinu. Samodejno aktivira "breakpoint" (prekinitveno točko), ko program doseže ta ukaz.',
                        example: "DEBUGPOINT"
                    },
                    build: {
                        parseFn(token, tokens, ast, ctx) {
                            buildLanguageKeywordDebugpoint(ast, ctx, token, tokens);
                        },
                    },
                    transform(tree, opts) {
                        return buildLanguageKeywordDebugpointWrapper(tree as TSyntaxTreeDebugPoint, opts.debug);
                    },
                },
            },
            instructions: {
                'ADD': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.ADD}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: arithmeticDescription('+'),
                        example: 'ADD 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildAddInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.ADD, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'SUB': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.SUB}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: arithmeticDescription('-'),
                        example: 'SUB 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildSubInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.SUB, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'MUL': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.MUL}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: arithmeticDescription('*'),
                        example: 'MUL 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildMulInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.MUL, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'DIV': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.DIV}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: arithmeticDescription('/'),
                        example: 'DIV 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildDivInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.DIV, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'GT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.GT}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: condArithmeticDescription('>'),
                        example: 'GT 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildGtInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.GT, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'GTE': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.GTE}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: condArithmeticDescription('>='),
                        example: 'GTE 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildGteInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.GTE, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'LT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.LT}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: condArithmeticDescription('<'),
                        example: 'LT 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildLtInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.LT, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'LTE': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.LTE}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: condArithmeticDescription('<='),
                        example: 'LTE 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildLteInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.LTE, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'EQ': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.EQ}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: condArithmeticDescription('=='),
                        example: 'EQ 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildEqInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.EQ, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'NEQ': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.NEQ}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticArgsDescription,
                        description: condArithmeticDescription('!='),
                        example: 'NEQ 40 2',
                    },
                    build: { args: 2 }, transform: (tree, opts) => buildNeqInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.NEQ, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'BIT_NEG': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.ANY_BITWISE_OP}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticBinaryOneArgDescription,
                        description: arithmeticBinaryDescription('~', undefined, true),
                        example: 'BIT_NEG 40 2',
                    },
                    build: { args: 1 },
                    transform:
                        (tree, opts) => buildInstructionBitNegWrapper({ cycles: CYCLES.INSTRUCTIONS.ANY_BITWISE_OP, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext })
                },
                'BIT_AND': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.ANY_BITWISE_OP}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticBinaryArgsDescription,
                        description: arithmeticBinaryDescription('&'),
                        example: 'BIT_AND 40 2',
                    },
                    build: { args: 2 },
                    transform:
                        (tree, opts) => buildInstructionBitAndWrapper({ cycles: CYCLES.INSTRUCTIONS.ANY_BITWISE_OP, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext })
                },
                'BIT_OR': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.ANY_BITWISE_OP}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticBinaryArgsDescription,
                        description: arithmeticBinaryDescription('|'),
                        example: 'BIT_OR 40 2',
                    },
                    build: { args: 2 },
                    transform:
                        (tree, opts) => buildInstructionBitOrWrapper({ cycles: CYCLES.INSTRUCTIONS.ANY_BITWISE_OP, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext })
                },
                'BIT_XOR': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.ANY_BITWISE_OP}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticBinaryArgsDescription,
                        description: arithmeticBinaryDescription('^'),
                        example: 'BIT_XOR 40 2',
                    },
                    build: { args: 2 },
                    transform:
                        (tree, opts) => buildInstructionBitExclusiveOrWrapper({ cycles: CYCLES.INSTRUCTIONS.ANY_BITWISE_OP, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext })
                },
                'BIT_SHIFT_L': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.ANY_BITWISE_OP}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticBinaryArgsDescription,
                        description: arithmeticBinaryDescription('<<'),
                        example: 'BIT_SHIFT_L 40 2',
                    },
                    build: { args: 2 },
                    transform:
                        (tree, opts) => buildInstructionBitShiftLeftWrapper({ cycles: CYCLES.INSTRUCTIONS.ANY_BITWISE_OP, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext })
                },
                'BIT_SHIFT_R': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.ANY_BITWISE_OP}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticBinaryArgsDescription,
                        description: arithmeticBinaryDescription('>>'),
                        example: 'BIT_SHIFT_R -40 2',
                    },
                    build: { args: 2 },
                    transform:
                        (tree, opts) => buildInstructionBitShiftRightWrapper({ cycles: CYCLES.INSTRUCTIONS.ANY_BITWISE_OP, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext })
                },
                'BIT_SHIFT_R_ZERO': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.ANY_BITWISE_OP}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: arithmeticBinaryArgsDescription,
                        description: arithmeticBinaryDescription('>>>', ' (ne ohrani predznaka - najbolj levi biti se vedno zapolnijo z 0)'),
                        example: 'BIT_SHIFT_R_ZERO -40 2',
                    },
                    build: { args: 2 },
                    transform:
                        (tree, opts) => buildInstructionBitShiftRightZeroFillWrapper({ cycles: CYCLES.INSTRUCTIONS.ANY_BITWISE_OP, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext })
                },
                'SET': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.SET} + ${listRefEvalArgNameCyclesDescription('cilj')}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: [
                            [ARG_TYPES_MAP.REGISTER_MEMORY('cilj', 'Kaj želimo nastaviti')],
                            [ARG_TYPES_MAP.NUMBER('vrednost', 'Vrednost ki jo dodelimo cilju')]
                        ],
                        description: 'Nastavi cilj in akumulator na podano vrednost ter ga vrne.',
                        example: 'SET @1 42',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionSet(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildSetInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.SET, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'CALL': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.CALL}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.FUNCTION_LABEL('ime_funkcije', 'funkcija ki jo želimo poklicati'),
                            ]
                        ],
                        description: 'Pokliče dano funkcijo in vrne rezultat v akumulatorju',
                        example: 'CALL meaning_of_life',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionCall(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildFunctionCallWrapper({ cycles: CYCLES.INSTRUCTIONS.CALL, tree, debug: opts.debug, astBuildContext: opts.astBuildContext })
                },
                'TRUNC': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.TRUNC}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: [
                            [ARG_TYPES_MAP.NUMBER('vrednost', 'Število, ki ga bomo rezali')],
                        ],
                        description: 'Nastavi akumulator na celoštevilski del podane vrednosti z odstranitvijo decimalnega dela (brez zaokroževanja).',
                        example: 'TRUNC 42.000001',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionTrunc(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildTruncInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.TRUNC, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'PUSH': {
                    metadata: {
                        cyclesDescription: `${batchAsCyclesDescription({ batchSize: globalInstructionsBatchSize, cyclesPerBatch: CYCLES.INSTRUCTIONS.PUSH })}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.LIST.NUMBER('seznam', 'Seznam, ki ga bomo potisnili na sklad'),
                                ARG_TYPES_MAP.STRING('niz', 'Zapis, ki ga bomo potisnili na sklad'),
                                ARG_TYPES_MAP.NUMBER('vrednost', 'Vrednost, ki jo bomo potisnili na sklad')
                            ]
                        ],
                        description: 'Potisne vhod na vrh sklada. Če je podan seznam ali niz, potisne od leve proti desni enote zaporedno na sklad.',
                        example: 'PUSH { 4 20 }',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPush(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPushInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.PUSH, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, reverse: false })
                },
                'PUSH_R': {
                    metadata: {
                        cyclesDescription: `${batchAsCyclesDescription({ batchSize: globalInstructionsBatchSize, cyclesPerBatch: CYCLES.INSTRUCTIONS.PUSH_R })}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.LIST.NUMBER('seznam', 'Seznam elementov, ki jih bomo potisnili na sklad'),
                                ARG_TYPES_MAP.STRING('niz', 'Zapis znakov, ki jih bomo potisnili na sklad'),
                                ARG_TYPES_MAP.NUMBER('vrednost', 'Vrednost, ki jo bomo potisnili na sklad')
                            ]
                        ],
                        description: 'Potisne vhodno vrednost na vrh sklada. Če je podan seznam ali niz, potisne od desne proti levi enote zaporedno na sklad.',
                        example: 'PUSH_R { 20 4 }',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPush(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPushInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.PUSH_R, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, reverse: true })
                },
                'POP': {
                    metadata: {
                        cyclesDescription: `${batchAsCyclesDescription({ batchSize: globalInstructionsBatchSize, cyclesPerBatch: CYCLES.INSTRUCTIONS.POP })} + ${listRefEvalAsCyclesDescription()}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.LIST.REGISTER_MEMORY('seznam', 'Seznam elementov, ki jih bomo prepisali iz sklada'),
                                ARG_TYPES_MAP.REGISTER_MEMORY('cilj', 'Vrednost, ki jo bomo prepisali iz sklada')
                            ]
                        ],
                        description: 'Vzame vrednost iz vrha sklada in z njeno vrednostjo prepiše vhodni argument. Če je podan seznam ali niz, jemlje od leve proti desni enote zaporedno iz sklada.',
                        example: 'POP { @4 [2] }',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPop(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPopInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.POP, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, reverse: false })
                },
                'POP_R': {
                    metadata: {
                        cyclesDescription: `${batchAsCyclesDescription({ batchSize: globalInstructionsBatchSize, cyclesPerBatch: CYCLES.INSTRUCTIONS.POP_R })} + ${listRefEvalAsCyclesDescription()}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.LIST.REGISTER_MEMORY('seznam', 'Seznam elementov, ki jih bomo prepisali iz sklada'),
                                ARG_TYPES_MAP.REGISTER_MEMORY('cilj', 'Vrednost, ki jo bomo prepisali iz sklada')
                            ]
                        ],
                        description: 'Vzame vrednost iz vrha sklada in z njeno vrednostjo prepiše vhodni argument. Če je podan seznam ali niz, jemlje od desne proti levi enote zaporedno iz sklada.',
                        example: 'POP_R { [2] @4 }',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPop(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPopInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.POP_R, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, reverse: true })
                },
                'STACK_SIZE': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.STACK_SIZE}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: true,
                        arguments: [],
                        description: `Trenutno velikost sklada zapiše v akumulator`,
                        example: 'STACK_SIZE',
                    },
                    build: { args: 0 }, transform: (tree, opts) => buildStackSizeInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.STACK_SIZE, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
                'PRINT_TEXT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.PRINT_TEXT}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.LIST.NUMBER('seznam', 'Seznam, ki ga bomo izpisali'),
                                ARG_TYPES_MAP.STRING('niz', 'Zapis, ki ga bomo izpisali'),
                                ARG_TYPES_MAP.NUMBER('vrednost', 'Vrednost, ki jo bomo izpisali')
                            ]
                        ],
                        description: 'Podane številčne vrednosti pretvori v znake (po njihvih kodnih vrednostih - sicer niz samo preslika v znak) in enega za drugim zapiše na zaslon.',
                        example: 'PRINT_TEXT `pozdravljen svet!`',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPrint(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPrintInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.PRINT_TEXT, tree, debug: opts.debug, astBuildContext: opts.astBuildContext, asText: true, newline: false })
                },
                'PRINTLN_TEXT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.PRINTLN_TEXT}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.LIST.NUMBER('seznam', 'Seznam, ki ga bomo izpisali'),
                                ARG_TYPES_MAP.STRING('niz', 'Zapis, ki ga bomo izpisali'),
                                ARG_TYPES_MAP.NUMBER('vrednost', 'Vrednost, ki jo bomo izpisali')
                            ]
                        ],
                        description: 'Podane številčne vrednosti pretvori v znake (po njihvih kodnih vrednostih - sicer niz samo preslika v znak) in enega za drugim zapiše na zaslon. Na koncu doda novo vrstico',
                        example: 'PRINTLN_TEXT `pozdravljen svet!`',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPrint(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPrintInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.PRINTLN_TEXT, tree, debug: opts.debug, astBuildContext: opts.astBuildContext, asText: true, newline: true })
                },
                'PRINT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.PRINT}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.LIST.NUMBER('seznam', 'Seznam, ki ga bomo izpisali'),
                                ARG_TYPES_MAP.STRING('niz', 'Zapis, ki ga bomo izpisali'),
                                ARG_TYPES_MAP.NUMBER('vrednost', 'Vrednost, ki jo bomo izpisali')
                            ]
                        ],
                        description: 'Podane številčne vrednosti (če je podan niz, uporabi kodne vrednosti znakov) eno za drugim izpiše na zaslon, ločene s presledkom.',
                        example: 'PRINT `pozdravljen svet!`',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPrint(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPrintInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.PRINT, tree, debug: opts.debug, astBuildContext: opts.astBuildContext, asText: false, newline: false })
                },
                'PRINTLN': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.PRINTLN}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.LIST.NUMBER('seznam', 'Seznam, ki ga bomo izpisali'),
                                ARG_TYPES_MAP.STRING('niz', 'Zapis, ki ga bomo izpisali'),
                                ARG_TYPES_MAP.NUMBER('vrednost', 'Vrednost, ki jo bomo izpisali')
                            ]
                        ],
                        description: 'Podane številčne vrednosti (če je podan niz, uporabi kodne vrednosti znakov) eno za drugim izpiše na zaslon, ločene s presledkom. Na koncu doda novo vrstico',
                        example: 'PRINTLN `pozdravljen svet!`',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPrint(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPrintInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.PRINTLN, tree, debug: opts.debug, astBuildContext: opts.astBuildContext, asText: false, newline: true })
                },
                'PROMPT': {
                    metadata: {
                        cyclesDescription: `${CYCLES.INSTRUCTIONS.PROMPT}`,
                        returnsAccumulator: true,
                        modifiesAccumulator: false,
                        arguments: [
                            [
                                ARG_TYPES_MAP.STRING('vprasanje', 'Kaj želimo uporabnika vprašati'),
                                ARG_TYPES_MAP.NUMBER('st_znakov', 'Število znakov našega vprašanja, ki se nahaja na skladu')
                            ],
                            [
                                ARG_TYPES_MAP.NUMBER('omejitev', 'Maksimalno število znakov, ki jih lahko uporabnik vpiše kot odgovor')
                            ]
                        ],
                        description: 'Postavi vprašanje uporabniku, če je podano št_znakov potem iz vrha sklada zaporedno prebere število znakov, da sestavi vprašanje. Omejitev pove koliko znakov lahko uporabnik največ vpiše. Na sklad kot rezultat najprej potisne znake, ki jih je uporabnik vpisal, nato pa potisne skupno število teh znakov.',
                        example: 'PROMPT `Kaj je smisel življenja?` 42',
                    },
                    build: {
                        parseArgsFn(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] { return buildLanguageInstructionPromptAst(tokens, ctx); }
                    },
                    transform: (tree, opts) => buildPromptInstructionWrapper({ cycles: CYCLES.INSTRUCTIONS.PROMPT, tree: tree, debug: opts.debug, astBuildContext: opts.astBuildContext, })
                },
            }
        }
    }

    return astContext;
}


/* BUILD & TRANSFORM SECTIONS FOR DEFAULT AST CONTEXT */

/*
    SECTION: [BUILD INSTRUCTIONS]
*/

function buildLanguageInstructionPrint(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] {
    consumeSpace(tokens);
    const next = tokens[0];
    if (!next) {
        throw new AstCompileError(`Pričakovan niz / seznam / izraz, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    if (next.type === 'STRING') {
        return [consumeString(tokens)];
    }

    if (isConsumableSymbolOf(next, '{')) {
        return [consumeList(tokens, tokens => consumeExpression(tokens, ctx))];
    }
    return [consumeExpression(tokens, ctx)];
}

function buildLanguageInstructionPush(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] {
    consumeSpace(tokens);
    const next = tokens[0];
    if (!next) {
        throw new AstCompileError(`Pričakovan niz / seznam / izraz, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    if (next.type === 'STRING') {
        return [consumeString(tokens)];
    }
    if (isConsumableSymbolOf(next, '{')) {
        return [consumeList(tokens, tokens => consumeExpression(tokens, ctx))];
    }
    return [consumeExpression(tokens, ctx)];
}

function buildLanguageInstructionPop(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] {
    consumeSpace(tokens);
    const next = tokens[0];
    if (isConsumableSymbolOf(next, '{')) {
        return [consumeList(tokens, tokens => consumeRegisterOrMemory(tokens, ctx))];
    }
    return [consumeRegisterOrMemory(tokens, ctx)];
}

function buildLanguageInstructionSet(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] {
    consumeSpace(tokens);
    const setLocation = consumeRegisterOrMemory(tokens, ctx);
    consumeSpace(tokens);
    const getLocation = consumeExpression(tokens, ctx);
    return [setLocation, getLocation];
}

function buildLanguageInstructionTrunc(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] {
    consumeSpace(tokens);
    const expr = consumeExpression(tokens, ctx);
    return [expr];
}



function buildLanguageInstructionCall(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] {
    consumeSpace(tokens);
    const functionNameToken = consumeLabel(tokens);
    (ctx.builderContext.functionCallsMap[functionNameToken.token] ??= []).push(functionNameToken);
    return [functionNameToken]
}

function buildLanguageInstructionPromptAst(tokens: TToken[], ctx: TSyntaxTreeBuilderContext): TSyntaxTree[] {
    consumeSpace(tokens);
    const next = tokens[0];
    if (!next) {
        throw new AstCompileError(`Pričakovan niz / izraz, vendar ni več znakov v programu.`, { eof: true }, { eof: true });
    }
    const promptTextTree: TSyntaxTree = next.type === 'STRING'
        ? consumeString(tokens)
        : consumeExpression(tokens, ctx);

    consumeSpace(tokens);
    const maxSizeTree = consumeExpression(tokens, ctx);
    return [promptTextTree, maxSizeTree]
}

/*
    SECTION: [BUILD KEYWORDS]
*/

function buildLanguageKeywordConst(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    if (ast.blockOf !== 'ROOT') {
        throw new AstCompileError(`Konstante je možno definirati samo izven blokov "{" ... "}"`, token, endLineCol(token));
    }
    consumeCurrentToken(tokens);
    consumeSpace(tokens);
    const labelToken = consumeLabel(tokens);
    const constantName = labelToken.token;
    if (ctx.builderContext.constantsMap[constantName]) {
        const existingConstant = ctx.builderContext.constantsMap[constantName];
        throw new AstCompileError(`Konstante je že bila definirana v vrstici: ${existingConstant.line}`, token, endLineCol(token));
    }
    consumeSpace(tokens);
    const nextToken = tokens[0];
    if (!nextToken) {
        throw new AstCompileError(`Pričakovano število, vendar ni več znakov v programu.`, endLineCol(token), { eof: true });
    }
    let constant = null;
    switch (nextToken.type) {
        case 'NUMBER': constant = consumeNumber(tokens); break;
        case 'ASCII': constant = consumeAsciiNumber(tokens); break;
        default:
            throw new AstCompileError(`Pričakovano število, vendar dobil "${nextToken.token}"`, nextToken, endLineCol(nextToken));
    }
    const constantItem: TSyntaxTreeConstant = { type: 'CONSTANT', name: constantName, value: constant, line: token.line, column: token.column };
    ctx.builderContext.constantsMap[constantName] = constantItem;
    ast.sequence.push(constantItem);
}

function buildLanguageKeywordContinue(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    if (!ast.canUseLoopControlStatements) {
        throw new AstCompileError(`Ni mogoče uporabiti "${token.token}", saj se ne nahaja znotraj zanke`, token, endLineCol(token));
    }
    consumeCurrentToken(tokens);
    ast.sequence.push({ type: 'CONTINUE', line: token.line, column: token.column, endPos: peekForEndPosition(tokens) });
}

function buildLanguageKeywordDebugpoint(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    consumeCurrentToken(tokens);
    ast.sequence.push({ type: 'DEBUGPOINT', line: token.line, column: token.column, endPos: peekForEndPosition(tokens) });
}

function buildLanguageKeywordBreak(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    if (!ast.canUseLoopControlStatements) {
        throw new AstCompileError(`Ni mogoče uporabiti "${token.token}", saj se ne nahaja znotraj zanke`, token, endLineCol(token));
    }
    consumeCurrentToken(tokens);
    ast.sequence.push({ type: 'BREAK', line: token.line, column: token.column, endPos: peekForEndPosition(tokens) });
}

function buildLanguageKeywordExit(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    if (!ast.canUseFunctionControlStatements) {
        throw new AstCompileError(`Ni mogoče uporabiti "${token.token}", saj se ne nahaja v funkciji`, token, endLineCol(token));
    }
    consumeCurrentToken(tokens);
    consumeSpace(tokens);
    const expression = consumeExpression(tokens, ctx);
    ast.sequence.push({ type: 'EXIT', expression, line: token.line, column: token.column, endPos: peekForEndPosition(tokens) });
}

function buildLanguageKeywordTerminate(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    consumeCurrentToken(tokens);
    consumeSpace(tokens);
    const expression = consumeExpression(tokens, ctx);
    ast.sequence.push({ type: 'TERMINATE', expression, line: token.line, column: token.column, endPos: peekForEndPosition(tokens) });
}

function buildLanguageKeywordFunction(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    if (ast.blockOf !== 'ROOT') {
        throw new AstCompileError(`Funkcije je možno definirati samo izven blokov "{" ... "}"`, token, endLineCol(token));
    }

    consumeCurrentToken(tokens);

    consumeSpace(tokens);

    const labelToken = consumeLabel(tokens);

    consumeIfSpaceElseIgnore(tokens);
    const blockToken = consumeBlockStart(tokens);
    const functionAst: TSyntaxTreeProgram = {
        type: 'PROGRAM', blockOf: 'FUNCTION', level: ast.level + 1, sequence: [],
        canUseLoopControlStatements: false, canUseFunctionControlStatements: true,
        line: blockToken.line, column: blockToken.column
    };
    parseNested(functionAst, tokens, ctx);
    consumeIfSpaceElseIgnore(tokens);
    consumeBlockEnd(tokens);

    const functionName = labelToken.token;
    if (ctx.builderContext.functionsMap[functionName]) {

        const existingFn = ctx.builderContext.functionsMap[functionName];
        throw new AstCompileError(`Funkcije "${functionName}" ni možno definirati, saj je ta že definirana v vrstici: ${existingFn.line}`, token, endLineCol(labelToken));
    }
    const functionDefinition: TSyntaxTreeFunction = {
        type: 'FUNCTION',
        name: functionName,
        label: labelToken,
        body: functionAst,
        line: token.line, column: token.column
    };
    ctx.builderContext.functionsMap[functionName] = functionDefinition;
    ast.sequence.push(functionDefinition);
}

function buildLanguageKeywordWhile(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    consumeCurrentToken(tokens);

    consumeSpace(tokens);
    const expression = consumeExpression(tokens, ctx);
    consumeIfSpaceElseIgnore(tokens);
    const blockToken = consumeBlockStart(tokens);
    const ifTrueAst: TSyntaxTreeProgram = {
        type: 'PROGRAM', blockOf: 'WHILE', level: ast.level + 1, sequence: [],
        canUseLoopControlStatements: true,
        canUseFunctionControlStatements: ast.canUseFunctionControlStatements,
        line: blockToken.line, column: blockToken.column
    };
    parseNested(ifTrueAst, tokens, ctx);
    consumeIfSpaceElseIgnore(tokens);
    consumeBlockEnd(tokens);

    ast.sequence.push({
        type: 'WHILE',
        expression,
        true: ifTrueAst,
        line: token.line, column: token.column,
        endPos: peekForEndPosition([blockToken])
    });
}

function buildLanguageKeywordIf(ast: TSyntaxTreeProgram, ctx: TSyntaxTreeBuilderContext, token: TToken, tokens: TToken[]): void {
    consumeCurrentToken(tokens);

    consumeSpace(tokens);
    const expression = consumeExpression(tokens, ctx);
    consumeIfSpaceElseIgnore(tokens);
    const blockToken = consumeBlockStart(tokens);
    const ifTrueAst: TSyntaxTreeProgram = {
        type: 'PROGRAM', blockOf: 'IF', level: ast.level + 1, sequence: [],
        canUseLoopControlStatements: ast.canUseLoopControlStatements,
        canUseFunctionControlStatements: ast.canUseFunctionControlStatements,
        line: blockToken.line, column: blockToken.column
    };
    parseNested(ifTrueAst, tokens, ctx);
    consumeIfSpaceElseIgnore(tokens);
    consumeBlockEnd(tokens);
    consumeIfSpaceElseIgnore(tokens);
    const maybeElseToken = tokens[0];
    let ifFalseAst: TSyntaxTreeProgram | null = null;
    if (maybeElseToken && maybeElseToken.type === 'KEYWORD' && maybeElseToken.token === 'ELSE') {
        consumeCurrentToken(tokens);
        consumeIfSpaceElseIgnore(tokens);
        const blockToken = consumeBlockStart(tokens);
        ifFalseAst = {
            type: 'PROGRAM', blockOf: 'ELSE', level: ast.level + 1, sequence: [],
            canUseLoopControlStatements: ast.canUseLoopControlStatements,
            canUseFunctionControlStatements: ast.canUseFunctionControlStatements,
            line: blockToken.line, column: blockToken.column
        };
        parseNested(ifFalseAst, tokens, ctx);
        consumeIfSpaceElseIgnore(tokens);
        consumeBlockEnd(tokens);
    }

    ast.sequence.push({
        type: 'IF',
        expression,
        true: ifTrueAst,
        false: ifFalseAst,
        line: token.line, column: token.column,
        endPos: peekForEndPosition([blockToken])
    });
}

/*
   SECTION: [TRANSFORM INSTRUCTIONS]
*/

function buildTruncInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (numExpr) => `Math.trunc(${numExpr})`);
}

function buildGtInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `Number(${leftExpr} > ${rightExpr})`);
}

function buildGteInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `Number(${leftExpr} >= ${rightExpr})`);
}

function buildLtInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `Number(${leftExpr} < ${rightExpr})`);
}

function buildLteInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `Number(${leftExpr} <= ${rightExpr})`);
}

function buildEqInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `Number(${leftExpr} === ${rightExpr})`);
}

function buildNeqInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `Number(${leftExpr} !== ${rightExpr})`);
}

function buildAddInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `${leftExpr} + ${rightExpr}`);
}

function buildSubInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `${leftExpr} - ${rightExpr}`);
}

function buildMulInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `${leftExpr} * ${rightExpr}`);
}

function buildDivInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (leftExpr, rightExpr) => `${leftExpr} / ${rightExpr}`);
}

function buildInstructionBitNegWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (expr) => `(~${expr})`);
}

function buildInstructionBitAndWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (left, right) => `(${left} & ${right})`);
}

function buildInstructionBitOrWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (left, right) => `(${left} | ${right})`);
}

function buildInstructionBitExclusiveOrWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (left, right) => `(${left} ^ ${right})`);
}

function buildInstructionBitShiftLeftWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (left, right) => `(${left} << ${right})`);
}

function buildInstructionBitShiftRightWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (left, right) => `(${left} >> ${right})`);
}

function buildInstructionBitShiftRightZeroFillWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, (left, right) => `(${left} >>> ${right})`);
}

function buildStackSizeInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }) {
    return buildGenericNumericAccumulatorInstructionWrapper(args, () => compileExe('stack.getSize'));
}

function buildPushInstructionWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean, reverse: boolean, astBuildContext: TSyntaxTreeBuilderContext, forceInstruction?: boolean }) {
    const { debug, tree, astBuildContext } = args;
    return buildGenericInstructionWrapper({ cycles: 0 /* cycles are calculated in internalCodeBody arg. */, tree, debug }, ([pushArg]) => {
        switch (pushArg.type) {
            case 'LIST':
                return buildGenericInstructionListArg({ debug, cyclesList: CYCLES.LIST_EXPR, astBuildContext }, pushArg);
            case 'STRING':
                return buildGenericInstructionStringArg({ debug, cyclesString: CYCLES.STRING }, pushArg);
            default:
                return `[${buildGenericInstructionExpressionArg({ debug, memRegOnly: CYCLES.LIST_EXPR.memRegOnly, astBuildContext }, pushArg)}]`;
        }
    }, `
${compileExe('cpuCycles.run<INSTRUCTION_BATCH>', { codeMap: tree }, { baseCycles: args.cycles, listSizeCode: 'args.length', batchSize: globalInstructionsBatchSize })}
for(${args.reverse ? `let i=args.length-1;i>=0;i--` : `let i=0;i<args.length;i++`}){
        ${compileExe('stack.push<CODE>', { codeMap: tree }, 'args[i]')};
}`);
}

function buildPopInstructionWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean, reverse: boolean, astBuildContext: TSyntaxTreeBuilderContext, forceInstruction?: boolean }) {
    const { debug, tree, astBuildContext } = args;
    return buildGenericInstructionWrapper({ cycles: 0 /* cycles are calculated in internalCodeBody arg. */, tree, debug }, ([popArg]) => {
        switch (popArg.type) {
            case 'LIST':
                return buildGenericInstructionListArg({ debug, cyclesList: CYCLES.LIST_MEM_REG_ONLY, astBuildContext }, popArg);
            default:
                return `[${buildGenericInstructionExpressionArg({ debug, memRegOnly: CYCLES.LIST_MEM_REG_ONLY.memRegOnly, astBuildContext }, popArg)}]`;
        }
    }, `
${compileExe('cpuCycles.run<INSTRUCTION_BATCH>', { codeMap: tree }, { baseCycles: args.cycles, listSizeCode: 'args.length', batchSize: globalInstructionsBatchSize })}
for(${args.reverse ? `let i=args.length-1;i>=0;i--` : `let i=0;i<args.length;i++`}){
    switch(args[i][0]) {
        case 0: 
            ${compileExe('registers.set<FN(OFFSET,VALUE)>', { codeMap: tree })}(args[i][1], ${compileExe('stack.pop', { codeMap: tree })}); 
            ${compileExe('cpuCycles.run', { codeMap: tree }, CYCLES.REG_READ_WRITE)}
            break;
        case 1: 
            ${compileExe('heap.set<FN(OFFSET,VALUE)>', { codeMap: tree })}(args[i][1], ${compileExe('stack.pop', { codeMap: tree })}); 
            ${compileExe('cpuCycles.run', { codeMap: tree }, CYCLES.MEMORY_READ_WRITE)}
            break;
        default: throw new Error('unknown data type');
    }
}`);
}

function buildSetInstructionWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext, forceInstruction?: boolean }) {
    const { debug, tree, astBuildContext } = args;
    return buildGenericInstructionWrapper({ cycles: args.cycles, tree, debug },
        ([destArg, fromArg]) => `[${buildGenericInstructionExpressionArg({ debug, memRegOnly: CYCLES.MEM_REG_ONLY, astBuildContext }, destArg)},${buildGenericInstructionExpressionArg({ debug, memRegOnly: false, astBuildContext }, fromArg)}]`,
        `switch(args[0][0]) {
            case 0: 
                ${compileExe('registers.set<FN(OFFSET,VALUE)>', { codeMap: tree })}(args[0][1], args[1]); 
                ${compileExe('cpuCycles.run', { codeMap: tree }, CYCLES.REG_READ_WRITE)}
                break;
            case 1: 
                ${compileExe('heap.set<FN(OFFSET,VALUE)>', { codeMap: tree })}(args[0][1], args[1]); 
                ${compileExe('cpuCycles.run', { codeMap: tree }, CYCLES.MEMORY_READ_WRITE)}
                break;
            default: throw new Error('unknown data type'); 
        }
        return ${compileExe('registers.setAccumulator<INTERNAL>', 'args[1]')};
    `);
}

function buildPromptInstructionWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext, forceInstruction?: boolean }) {
    const { debug, tree, astBuildContext } = args;
    return buildGenericInstructionWrapper({ cycles: args.cycles, tree, debug },
        ([displayArg, maxSizeArg]) => {
            const displayCodeArg = (() => {
                switch (displayArg.type) {
                    case 'STRING':
                        return `[0,${buildGenericInstructionStringArg({ debug, cyclesString: CYCLES.STRING }, displayArg)}]`;
                    default:
                        return `[1,${buildGenericInstructionExpressionArg({ debug, memRegOnly: false, astBuildContext }, displayArg)}]`
                }
            })();
            const maxSizeCodeArg = `${buildGenericInstructionExpressionArg({ debug, memRegOnly: false, astBuildContext }, maxSizeArg)}`;
            return `[${displayCodeArg},${maxSizeCodeArg}]`;
        },
        // proper prompting can only work good in debug mode for now, as we need to give ui some time to refresh.
        // in release it will still work, but it will be ahead terminal output.
        `
        let display = null;
        switch(args[0][0]) {
            case 0: { 
                display = args[0][1].map(v => String.fromCharCode(v)).join('');
                break;
            }
            case 1: { 
                let stackSize = args[0][1];
                display = '';
                while(stackSize > 0) {
                    display = String.fromCharCode(${compileExe('stack.pop', { codeMap: tree })}) + display;
                    --stackSize;
                }
                break;
            }
            default: throw new Error('unsupported')
        }
        
        let readStackSize = args[1];
        
        const userInputChars = ${debug ? '(await runtimeLib.debuggerPrompt(display))' : '(runtimeLib.prompt(display))'}.split('').map(v => v.charCodeAt(0));
        const userLen = userInputChars.length;
        const endLen = Math.max(0,Math.min(readStackSize, userLen))
        for(let i=endLen-1;i>=0;i--){
            ${compileExe('stack.push<CODE>', { codeMap: tree }, 'userInputChars[i]')};
        }
        ${compileExe('stack.push<CODE>', { codeMap: tree }, 'endLen')};
        return ${compileExe('registers.getAccumulator<INTERNAL>')};
    `
    );
}

function buildPrintInstructionWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext, forceInstruction?: boolean, asText: boolean, newline: boolean }) {
    const { debug, tree, astBuildContext } = args;
    const printAs = args.asText ? 'printText' : 'print'
    const nlPrint = args.newline ? `runtimeLib.printText(${'\n'.charCodeAt(0)})` : ''
    return buildGenericInstructionWrapper({ cycles: args.cycles, tree, debug },
        ([printArg]) => {
            switch (printArg.type) {
                case 'LIST':
                    return buildGenericInstructionListArg({ debug, cyclesList: CYCLES.LIST_EXPR, astBuildContext }, printArg);
                case 'STRING':
                    return buildGenericInstructionStringArg({ debug, cyclesString: CYCLES.STRING }, printArg);
                default:
                    return `[${buildGenericInstructionExpressionArg({ debug, memRegOnly: false, astBuildContext }, printArg)}]`;
            }
        },
        `runtimeLib.${printAs}(...args);${nlPrint};`);
}

function buildFunctionCallWrapper(args: { cycles: number, astBuildContext: TSyntaxTreeBuilderContext, tree: TSyntaxTreeInstruction, debug: boolean }) {
    // function always returns "set" accumulator, so no need to set it!
    const label = args.tree.args[0] as TSyntaxTreeLabel;
    // we must execute label consumtion to add cost for identifyng function!
    const labelWrapper = buildWrapper({ cycles: CYCLES.CONSTANT_READ_WRITE, tree: label, debug: args.debug, forceInstruction: false }, `(null)`)
    const codeMap = buildCodeMap(args.tree)
    const statCode = `{ cycles: ${args.cycles}, codeMap: ${buildCodeMap(args.tree)} }`;
    if (args.debug) {
        return `(await WRAPPER_LAZY(${statCode},${injectWrapperDebuggerBefore(args.tree)},${labelWrapper},${injectWrapperDebuggerAfter(args.tree)},async ()=>{ return await $elf_${label.token}(${codeMap}); }))`;
    } else {
        return `(WRAPPER_LAZY(${statCode},${labelWrapper},()=>{ return $elf_${label.token}(${codeMap}); }))`;
    }
}

/*
   SECTION: [TRANSFORM KEYWORDS]
*/

function buildLanguageKeywordConstWrapper(tree: TSyntaxTreeConstant, debug: boolean) {
    return ''; // no need to transpile as value is already resolved in tree and assigned to constant labels trees.
}

function buildLanguageKeywordContinueWrapper(tree: TSyntaxTreeContinue, debug: boolean): string {
    return buildEagerInlineWrapper({ cycles: CYCLES.KEYWORDS.CONTINUE, tree, debug }, "(null)", (code) => `\n${code};\ncontinue;\n`);
}

function buildLanguageKeywordDebugpointWrapper(tree: TSyntaxTreeDebugPoint, debug: boolean): string {
    if (debug) {
        // should not eat up cycles!
        // it will automatically invoke debugger, as tree type is "debugpoint".
        return buildEagerInlineWrapper({ cycles: CYCLES.KEYWORDS.DEBUGPOINT, tree, debug }, "(null)", (code) => `\n${code};\n`);
    }
    return '';
}

function buildLanguageKeywordBreakWrapper(tree: TSyntaxTreeBreak, debug: boolean): string {
    return buildEagerInlineWrapper({ cycles: CYCLES.KEYWORDS.BREAK, tree, debug }, "(null)", (code) => `\n${code};\nbreak;\n`);
}

function buildLanguageKeywordExitWrapper(tree: TSyntaxTreeExit, astBuildContext: TSyntaxTreeBuilderContext, debug: boolean): string {
    return buildEagerInlineWrapper({ cycles: CYCLES.KEYWORDS.EXIT, tree, debug }, compileExe('registers.setAccumulator<INTERNAL>', buildExpression(tree.expression, { debug, astBuildContext })), (code) => `return ${code}`)
}

function buildLanguageKeywordTerminateWrapper(tree: TSyntaxTreeTerminate, astBuildContext: TSyntaxTreeBuilderContext, debug: boolean): string {
    return buildEagerInlineWrapper({ cycles: CYCLES.KEYWORDS.TERMINATE, tree, debug }, compileExe('registers.setAccumulator<INTERNAL>', buildExpression(tree.expression, { debug, astBuildContext })), (code) => `throw new ElfTerminateError("TERMINATED", ${code}, false, ${buildCodeMap(tree)})`)
}

function buildLanguageKeywordFunctionWrapper(tree: TSyntaxTreeFunction, astBuildContext: TSyntaxTreeBuilderContext, debug: boolean): string {
    const asyncKw = debug ? 'async' : ''
    const awaitKw = debug ? 'await' : ''
    return `\n${asyncKw} function $elf_${tree.name}(codeMap) { 
                        ${compileExe('callStack.push<INTERNAL>', { codeMap: 'codeMap' }, tree.name)};
                        ${awaitKw} (${asyncKw} ()=>{
                            ${buildProgram(tree.body, { debug, astBuildContext })}
                        })(); 
                        ${compileExe('callStack.pop<INTERNAL>')};
                        return ${compileExe("registers.getAccumulator<INTERNAL>")};
                    }\n`;
}

function buildLanguageKeywordIfWrapper(tree: TSyntaxTreeIf, astBuildContext: TSyntaxTreeBuilderContext, debug: boolean): string {
    const evalIfExpr = buildExpression(tree.expression, { isParentControlStmt: true, debug, astBuildContext });
    const ifExprWrapper = buildWrapper({ cycles: CYCLES.KEYWORDS.IF, tree: tree, debug }, `(${evalIfExpr})`);
    return `if(${ifExprWrapper}) {
    ${buildProgram(tree.true, { debug, astBuildContext })}
} else {
    ${tree.false ? buildProgram(tree.false, { debug, astBuildContext }) : ''}
}\n`
}

function buildLanguageKeywordWhileWrapper(tree: TSyntaxTreeWhile, astBuildContext: TSyntaxTreeBuilderContext, debug: boolean): string {
    const evalIfExpr = buildExpression(tree.expression, { isParentControlStmt: true, debug, astBuildContext });
    const whileExprWrapper = buildWrapper({ cycles: CYCLES.KEYWORDS.WHILE, tree: tree, debug }, `(${evalIfExpr})`);
    return `while(1) {
    if(${whileExprWrapper}) {
        ${buildProgram(tree.true, { debug, astBuildContext })}
    } else {
        break;
    }
}\n`;
}

/*
   SECTION: [TRANSFORM PRIMITIVES]
*/

function buildLanguagePrimitiveNumberWrapper(tree: TSyntaxTreeNumber, opts: { astBuildContext: TSyntaxTreeBuilderContext, debug: boolean, isParentControlStmt?: boolean }) {
    const { debug, isParentControlStmt } = opts;
    return buildWrapper({ cycles: CYCLES.CONSTANT_READ_WRITE, tree: tree, debug, forceInstruction: isParentControlStmt }, `(${tree.value})`);
}

function buildLanguagePrimitiveLabelConstantWrapper(tree: TSyntaxTreeLabel, opts: { astBuildContext: TSyntaxTreeBuilderContext, debug: boolean, isParentControlStmt?: boolean }) {
    const { debug, isParentControlStmt } = opts;
    if (tree.valueIfConstant === undefined) {
        throw new Error('COMPILE ERROR, EXPECTED CONSTANT LABEL, BUT GOT FUNCTION / UNDEFINED label');
    }
    return buildWrapper({ cycles: CYCLES.CONSTANT_READ_WRITE, tree, debug, forceInstruction: isParentControlStmt }, `(${tree.valueIfConstant})`);
}

function buildLanguagePrimitiveRegisterWrapper(tree: TSyntaxTreeRegister, opts: { astBuildContext: TSyntaxTreeBuilderContext, debug: boolean, isParentControlStmt?: boolean }) {
    const { debug, isParentControlStmt } = opts;
    return buildWrapper({ cycles: CYCLES.REG_READ_WRITE, tree, debug, forceInstruction: isParentControlStmt }, `(${compileExe('registers.get<NUMBER>', { codeMap: tree }, tree.registerNumber)})`);
}

function buildLanguagePrimitiveMemoryWrapper(tree: TSyntaxTreeMemory, opts: { astBuildContext: TSyntaxTreeBuilderContext, debug: boolean, isParentControlStmt?: boolean }) {
    const { astBuildContext, debug, isParentControlStmt } = opts;
    return buildWrapper({ cycles: CYCLES.MEMORY_READ_WRITE, tree, debug, forceInstruction: isParentControlStmt }, `(${compileExe('heap.get', { codeMap: tree, debug, astBuildContext }, tree.innerExpression)})`);
}
